import { useEffect, useRef, useState } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { CANDIDATE_STATUS_TREE } from "../../utils/candidateStatuses";
import {
    EXPERIENCE_OPTIONS, FUNNEL_TABS, GENDER_OPTIONS, SORT_OPTIONS,
    activeFilterChips, moreFiltersCount, normalizeFilters,
} from "../../utils/candidateFunnel";

/**
 * Funnel tabs + compact filters of «Кандидаты» (правка №4, Чат 1). [Claude] 2026-11-03
 * Main filters stay in one line (search, vacancies, statuses, sort); the rest is under «Ещё фильтры».
 * Every active filter is a removable chip; «Сбросить» clears them all (search and sort stay).
 */
export const STATUS_OPTIONS = CANDIDATE_STATUS_TREE.flatMap((section) =>
    section.children.map((item) => ({ value: item.value, label: item.value, group: section.label })));

export function FunnelTabs({ tab, counts, onChange }) {
    return <div className="flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist" data-testid="candidate-funnel-tabs">
        {FUNNEL_TABS.map((item) => {
            const active = item.key === tab;
            const count = counts ? counts[item.key] : null;
            return <button key={item.key} type="button" role="tab" aria-selected={active} data-tab={item.key}
                onClick={() => onChange(item.key)}
                className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${active ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                {item.label}
                {typeof count === "number" ? <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-500"}`}>{count}</span> : null}
            </button>;
        })}
    </div>;
}

const field = "h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-amber-400";

function RangeInputs({ label, from, to, onFrom, onTo, step = 1, suffix = "" }) {
    return <div>
        <div className="mb-1 text-xs font-semibold text-slate-500">{label}</div>
        <div className="flex items-center gap-2">
            <input type="number" min={0} step={step} value={from} onChange={(e) => onFrom(e.target.value)} placeholder="от" className={field} />
            <span className="text-slate-400">—</span>
            <input type="number" min={0} step={step} value={to} onChange={(e) => onTo(e.target.value)} placeholder="до" className={field} />
            {suffix ? <span className="text-sm text-slate-400">{suffix}</span> : null}
        </div>
    </div>;
}

function MoreFilters({ filters, onChange, onClose }) {
    const [draft, setDraft] = useState(filters);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [onClose]);
    const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
    const toggleExp = (value) => set({ experience: draft.experience.includes(value) ? draft.experience.filter((v) => v !== value) : [...draft.experience, value] });
    const apply = () => {
        onChange({
            age_from: draft.age_from, age_to: draft.age_to, salary_from: draft.salary_from, salary_to: draft.salary_to,
            city: draft.city, gender: draft.gender, experience: draft.experience, ai_score_min: draft.ai_score_min,
            applied_from: draft.applied_from, applied_to: draft.applied_to, perfect: draft.perfect, not_sync: draft.not_sync,
        });
        onClose();
    };
    return <div ref={ref} className="absolute right-0 z-40 mt-1 w-[560px] max-w-[94vw] rounded-xl border border-slate-200 bg-white p-4 shadow-xl" data-testid="candidate-more-filters"
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") apply(); }}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RangeInputs label="Возраст" from={draft.age_from} to={draft.age_to} onFrom={(v) => set({ age_from: v })} onTo={(v) => set({ age_to: v })} />
            <RangeInputs label="Ожидаемая зарплата" from={draft.salary_from} to={draft.salary_to} step={5000} suffix="₽"
                onFrom={(v) => set({ salary_from: v })} onTo={(v) => set({ salary_to: v })} />
            <div>
                <div className="mb-1 text-xs font-semibold text-slate-500">Город</div>
                <input value={draft.city} onChange={(e) => set({ city: e.target.value })} placeholder="Например, Пермь" className={field} />
            </div>
            <div>
                <div className="mb-1 text-xs font-semibold text-slate-500">Пол</div>
                <select value={draft.gender} onChange={(e) => set({ gender: e.target.value })} className={field}>
                    {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>
            <div className="sm:col-span-2">
                <div className="mb-1 text-xs font-semibold text-slate-500">Опыт работы</div>
                <div className="flex flex-wrap gap-1.5">
                    {EXPERIENCE_OPTIONS.map((o) => {
                        const on = draft.experience.includes(o.value);
                        return <button key={o.value} type="button" onClick={() => toggleExp(o.value)}
                            className={`rounded-lg border px-2.5 py-1 text-sm ${on ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{o.label}</button>;
                    })}
                </div>
            </div>
            <div>
                <div className="mb-1 text-xs font-semibold text-slate-500">Дата отклика</div>
                <div className="flex items-center gap-2">
                    <input type="date" value={draft.applied_from} onChange={(e) => set({ applied_from: e.target.value })} className={field} />
                    <span className="text-slate-400">—</span>
                    <input type="date" value={draft.applied_to} onChange={(e) => set({ applied_to: e.target.value })} className={field} />
                </div>
            </div>
            <div>
                <div className="mb-1 text-xs font-semibold text-slate-500">ИИ-оценка резюме, от</div>
                <input type="number" min={0} max={100} value={draft.ai_score_min} onChange={(e) => set({ ai_score_min: e.target.value })} placeholder="0–100" className={field} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={draft.perfect} onChange={(e) => set({ perfect: e.target.checked })} className="h-4 w-4 accent-amber-500" />
                Только идеальные кандидаты
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={draft.not_sync} onChange={(e) => set({ not_sync: e.target.checked })} className="h-4 w-4 accent-amber-500" />
                Не синхронизированы с hh.ru
            </label>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">Отмена</button>
            <button type="button" onClick={apply} data-testid="candidate-more-filters-apply"
                className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-slate-800">Применить</button>
        </div>
    </div>;
}

export default function CandidatesToolbar({
    filters: rawFilters, onChange, onReset, vacancyOptions, vacanciesLoading, unboundHhCount = 0, vacancyName,
}) {
    const filters = normalizeFilters(rawFilters);
    const [search, setSearch] = useState(filters.search);
    const [moreOpen, setMoreOpen] = useState(false);
    const lastSent = useRef(filters.search);

    useEffect(() => {
        if (filters.search !== lastSent.current) {
            lastSent.current = filters.search;
            setSearch(filters.search);
        }
    }, [filters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = search.trim();
            if (value !== filters.search) {
                lastSent.current = value;
                onChange({ search: value });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const chips = activeFilterChips(filters, { vacancyName });
    const more = moreFiltersCount(filters);
    const sortValue = `${filters.sort_by}:${filters.sort_dir}`;

    return <div className="space-y-2" data-testid="candidates-toolbar">
        <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
                <svg className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ФИО, телефон, email, Telegram, навыки"
                    data-testid="candidates-search"
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-amber-400" />
            </div>
            <MultiSelectDropdown label="Вакансии" testId="candidates-vacancy-filter" loading={vacanciesLoading}
                options={(vacancyOptions || []).map((v) => ({ value: String(v.id), label: v.name || `Вакансия #${v.id}` }))}
                selected={filters.vacancy_ids} onChange={(ids) => onChange({ vacancy_ids: ids })}
                placeholder="Найти вакансию" emptyText="Вакансии не найдены"
                footer={unboundHhCount ? <span className="text-[11px] text-slate-400" title="Вакансии hh.ru без карточки в платформе">+{unboundHhCount} на hh без привязки</span> : null} />
            <MultiSelectDropdown label="Статусы" testId="candidates-status-filter" options={STATUS_OPTIONS}
                selected={filters.statuses} onChange={(statuses) => onChange({ statuses })} placeholder="Найти статус" />
            <div className="relative">
                <button type="button" onClick={() => setMoreOpen((v) => !v)} data-testid="candidate-more-filters-button"
                    className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-medium ${more ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                    Ещё фильтры{more ? <span className="rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">{more}</span> : null}
                </button>
                {moreOpen ? <MoreFilters filters={filters} onChange={onChange} onClose={() => setMoreOpen(false)} /> : null}
            </div>
            <select value={sortValue} onChange={(e) => { const [sort_by, sort_dir] = e.target.value.split(":"); onChange({ sort_by, sort_dir }); }}
                data-testid="candidates-sort" aria-label="Сортировка"
                className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 outline-none focus:border-amber-400">
                {SORT_OPTIONS.some((o) => o.value === sortValue) ? null : <option value={sortValue}>Своя сортировка</option>}
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
        {chips.length ? <div className="flex flex-wrap items-center gap-1.5" data-testid="candidate-filter-chips">
            {chips.map((chip) => <span key={chip.id} className="inline-flex max-w-[280px] items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 pl-2.5 pr-1 text-xs text-slate-700">
                <span className="truncate" title={chip.label}>{chip.label}</span>
                <button type="button" onClick={() => onChange(chip.patch)} aria-label={`Убрать фильтр «${chip.label}»`}
                    className="rounded-full px-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700">×</button>
            </span>)}
            <button type="button" onClick={onReset} data-testid="candidate-filters-reset" className="ml-1 text-xs font-semibold text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline">Сбросить все</button>
        </div> : null}
    </div>;
}
